import axios from "axios";
import { authHeaders, isTokenExpiring, getToken, refreshAccessToken } from "./authRefresh";

const API = window.location.origin;

// Satir arka plan renkleri (ARGB hex, # yok) — seat_bucket / iptal
const BUCKET_FILL = {
  vip_cam: "F3E8FF",   // VIP Cam Kenarı (mor)
  vip: "EDE9FE",       // VIP (menekşe)
  cam: "FEF3C7",       // Cam Kenarı (amber)
  sahne: "DBEAFE",     // Sahne (mavi)
  salon: "F1F5F9",     // Salon (açık gri)
};
const CANCEL_FILL = "FEE2E2"; // iptal (kırmızı)
const HEADER_FILL = "1E293B"; // koyu slate

const hexClean = (c) => String(c || "").replace("#", "").toUpperCase().slice(0, 6) || "E2E8F0";
const thin = { style: "thin", color: { rgb: "E2E8F0" } };
const BORDER = { top: thin, bottom: thin, left: thin, right: thin };

// Canli defteri (secili tarih + AKTIF kategori) tekneye gore gruplu, RENKLI bir .xlsx olarak indirir.
export async function exportSheetToExcel({ date, activeCategory = null, data = null } = {}) {
  const XLSXMod = await import("xlsx-js-style");
  const XLSX = XLSXMod.default || XLSXMod;

  if (!data) {
    if (isTokenExpiring(getToken())) { await refreshAccessToken(); }
    let url = `${API}/api/sheet/fortune/${date}?group=1&`;
    if (activeCategory === "unmapped") url += "unmapped=1&";
    else if (activeCategory != null) url += `category_id=${activeCategory}&`;
    const res = await axios.get(url, { headers: authHeaders(), validateStatus: () => true });
    if (res.status !== 200) throw new Error(res.data?.detail || "Veri alınamadı");
    data = res.data;
  }

  const wb = XLSX.utils.book_new();
  const catName = (data.categories || []).find((c) => c.id === data.active_category_id)?.name || "Defter";
  _appendSheet(XLSX, wb, data, safeSheetName(date) || "Defter");
  XLSX.writeFile(wb, `LuferDefter_${date}_${safeName(catName)}.xlsx`);
}

const safeName = (s) => String(s || "").replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 28);
// Excel sekme adı: yasak karakterler (: \ / ? * [ ]) yok, <=31 karakter
const safeSheetName = (s) => String(s || "").replace(/[:\\/?*[\]]/g, "-").slice(0, 31);

// Tek bir gün verisini (fortune) stilli bir worksheet'e çevirip workbook'a EKLER.
function _appendSheet(XLSX, wb, data, sheetName) {
  const columns = data.columns || [];
  const rows = data.rows || [];
  const headersSorted = [...(data.group_headers || [])].sort((a, b) => a.r - b.r);
  const nCols = columns.length;
  const aoa = [];
  const meta = [];
  aoa.push(columns.map((c) => c.label || c.key));
  meta.push({ type: "header" });
  const pushData = (row) => {
    aoa.push(columns.map((c) => { const v = row.values?.[c.key]; return v == null ? "" : v; }));
    const fill = row.cancelled ? CANCEL_FILL : (BUCKET_FILL[row.seat_bucket] || null);
    meta.push({ type: "data", fill, cancelled: !!row.cancelled });
  };
  if (headersSorted.length > 0) {
    headersSorted.forEach((h, i) => {
      const nextR = i + 1 < headersSorted.length ? headersSorted[i + 1].r : Infinity;
      const secRows = rows.filter((r) => r.r > h.r && r.r < nextR);
      const grp = new Array(nCols).fill("");
      grp[0] = `${h.name}  ·  ${h.count} rez  ·  ${h.pax} kişi`;
      aoa.push(grp);
      meta.push({ type: "group", fill: hexClean(h.bg) });
      secRows.forEach(pushData);
    });
  } else {
    rows.forEach(pushData);
  }
  if (rows.length === 0) { aoa.push(["Bu tarihte kayıt yok"]); meta.push({ type: "data", fill: null, cancelled: false }); }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = columns.map((c) => {
    const wide = ["isim", "otel_adi", "opsiyon", "ekstra", "aciklama"].includes(c.key);
    const mid = ["kod", "acenta"].includes(c.key);
    return { wch: wide ? 26 : mid ? 14 : 7 };
  });
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  if (nCols > 0) ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(0, nCols - 1) } }) };
  for (let r = 0; r < meta.length; r++) {
    const m = meta[r];
    for (let c = 0; c < Math.max(nCols, 1); c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { t: "s", v: "" };
      const cell = ws[ref];
      if (m.type === "header") {
        cell.s = { fill: { fgColor: { rgb: HEADER_FILL } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, alignment: { horizontal: "center", vertical: "center" }, border: BORDER };
      } else if (m.type === "group") {
        cell.s = { fill: { fgColor: { rgb: m.fill } }, font: { bold: true, color: { rgb: "1E293B" }, sz: 12 }, alignment: { vertical: "center" }, border: BORDER };
      } else {
        const s = { border: BORDER, alignment: { vertical: "center", wrapText: false } };
        if (m.fill) s.fill = { fgColor: { rgb: m.fill } };
        if (m.cancelled) s.font = { color: { rgb: "B91C1C" }, strike: true };
        cell.s = s;
      }
    }
  }
  // Sekme adı benzersiz olmalı (Excel aynı adı reddeder)
  let name = safeSheetName(sheetName) || "Sayfa";
  let i = 2; const used = wb.SheetNames || [];
  while (used.includes(name)) { name = `${safeSheetName(sheetName).slice(0, 28)}_${i}`; i++; }
  XLSX.utils.book_append_sheet(wb, ws, name);
}

// TARİH ARALIĞI: start..end her gün için AYRI SEKME (Canlı Sayfa görünümü) — tek .xlsx.
// onProgress(done, total) ile ilerleme bildirilir.
export async function exportSheetRangeToExcel({ start, end, activeCategory = null, onProgress = null } = {}) {
  const XLSXMod = await import("xlsx-js-style");
  const XLSX = XLSXMod.default || XLSXMod;
  // Gün listesi (dahil)
  const dates = [];
  let d0 = new Date(start + "T00:00:00"), d1 = new Date(end + "T00:00:00");
  if (isNaN(d0) || isNaN(d1)) throw new Error("Geçersiz tarih");
  if (d0 > d1) { const t = d0; d0 = d1; d1 = t; }
  const MAX_DAYS = 92;
  for (let d = new Date(d0); d <= d1 && dates.length < MAX_DAYS; d.setDate(d.getDate() + 1)) {
    dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  if (isTokenExpiring(getToken())) { await refreshAccessToken(); }
  const wb = XLSX.utils.book_new();
  let done = 0;
  for (const date of dates) {
    let url = `${API}/api/sheet/fortune/${date}?group=1&`;
    if (activeCategory === "unmapped") url += "unmapped=1&";
    else if (activeCategory != null) url += `category_id=${activeCategory}&`;
    try {
      const res = await axios.get(url, { headers: authHeaders(), validateStatus: () => true });
      const data = res.status === 200 ? res.data : { columns: [], rows: [] };
      const [, mm, dd] = date.split("-");
      _appendSheet(XLSX, wb, data, `${dd}.${mm}`);
    } catch {
      _appendSheet(XLSX, wb, { columns: [], rows: [] }, `${date.slice(5)}`);
    }
    done++; if (onProgress) onProgress(done, dates.length);
  }
  if (!wb.SheetNames.length) throw new Error("Aralıkta veri yok");
  XLSX.writeFile(wb, `LuferDefter_${start}_${end}.xlsx`);
}

// TRANSFER LISTESI: sadece transferli rezleri (otel adinda "kendi gelir" OLMAYANLAR) icerir.
// Sutunlar (Dinner sirasina gore A,B,C,D,F,W,X): Gemi, Otel, Acenta, Pax, Opsiyon, Telefon, Isim.
// Iptal edilen rezler transfer listesine ALINMAZ (araba gonderilmez).
const TRANSFER_COL_KEYS = ["gemi", "otel_adi", "acenta", "pax", "opsiyon", "telefon", "isim"];

const _isKendiGelir = (row) => {
  const norm = String(row?.values?.otel_adi || "")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return norm.includes("kendi gelir") || norm.replace(/\s+/g, "").includes("kendigelir");
};

export async function exportTransferListToExcel({ date, activeCategory = null, data = null } = {}) {
  const XLSXMod = await import("xlsx-js-style");
  const XLSX = XLSXMod.default || XLSXMod;

  if (!data) {
    if (isTokenExpiring(getToken())) { await refreshAccessToken(); }
    let url = `${API}/api/sheet/fortune/${date}?group=1&`;
    if (activeCategory === "unmapped") url += "unmapped=1&";
    else if (activeCategory != null) url += `category_id=${activeCategory}&`;
    const res = await axios.get(url, { headers: authHeaders(), validateStatus: () => true });
    if (res.status !== 200) throw new Error(res.data?.detail || "Veri alınamadı");
    data = res.data;
  }

  const allColumns = data.columns || [];
  const columns = TRANSFER_COL_KEYS.map((k) => allColumns.find((c) => c.key === k)).filter(Boolean);
  const nCols = columns.length;
  const rows = data.rows || [];
  const headersSorted = [...(data.group_headers || [])].sort((a, b) => a.r - b.r);
  const includeRow = (r) => !r.cancelled && !_isKendiGelir(r);

  const aoa = [];
  const meta = [];
  aoa.push(columns.map((c) => c.label || c.key));
  meta.push({ type: "header" });

  const pushData = (row) => {
    aoa.push(columns.map((c) => { const v = row.values?.[c.key]; return v == null ? "" : v; }));
    meta.push({ type: "data", fill: BUCKET_FILL[row.seat_bucket] || null });
  };

  if (headersSorted.length > 0) {
    headersSorted.forEach((h, i) => {
      const nextR = i + 1 < headersSorted.length ? headersSorted[i + 1].r : Infinity;
      const secRows = rows.filter((r) => r.r > h.r && r.r < nextR).filter(includeRow);
      if (secRows.length === 0) return; // transferli rez yoksa tekne grubunu atla
      const totalPax = secRows.reduce((s, r) => s + (parseInt(r.values?.pax) || 0), 0);
      const grp = new Array(nCols).fill("");
      grp[0] = `${h.name}  ·  ${secRows.length} transfer  ·  ${totalPax} kişi`;
      aoa.push(grp);
      meta.push({ type: "group", fill: hexClean(h.bg) });
      secRows.forEach(pushData);
    });
  } else {
    rows.filter(includeRow).forEach(pushData);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = columns.map((c) => {
    const wide = ["isim", "otel_adi", "opsiyon"].includes(c.key);
    const mid = ["acenta", "telefon"].includes(c.key);
    return { wch: wide ? 28 : mid ? 15 : 8 };
  });
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(0, nCols - 1) } }) };

  for (let r = 0; r < meta.length; r++) {
    const m = meta[r];
    for (let c = 0; c < nCols; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { t: "s", v: "" };
      const cell = ws[ref];
      if (m.type === "header") {
        cell.s = { fill: { fgColor: { rgb: HEADER_FILL } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, alignment: { horizontal: "center", vertical: "center" }, border: BORDER };
      } else if (m.type === "group") {
        cell.s = { fill: { fgColor: { rgb: m.fill } }, font: { bold: true, color: { rgb: "1E293B" }, sz: 12 }, alignment: { vertical: "center" }, border: BORDER };
      } else {
        const s = { border: BORDER, alignment: { vertical: "center", wrapText: false } };
        if (m.fill) s.fill = { fgColor: { rgb: m.fill } };
        cell.s = s;
      }
    }
  }

  const wb = XLSX.utils.book_new();
  const catName = (data.categories || []).find((c) => c.id === data.active_category_id)?.name || "Defter";
  const safe = (s) => String(s || "").replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 28);
  XLSX.utils.book_append_sheet(wb, ws, "Transfer");
  XLSX.writeFile(wb, `LuferTransfer_${date}_${safe(catName)}.xlsx`);
}
